import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Send } from "lucide-react";
import { LegalLayout } from "../components/LegalLayout";
import { CONTACT_PAGE } from "../data/legalPages";
import { COMPANY } from "../data/company";
import { api } from "../lib/api";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-black/10 bg-white/70 focus:outline-none focus:ring-2 focus:ring-[#1A3B2B]/30 text-sm";

const EmailCard = () => (
  <a
    href={`mailto:${COMPANY.email}`}
    className="flex items-center gap-4 p-6 rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] mb-12 hover:bg-[#15311f] transition-colors"
    data-testid="contact-email-card"
  >
    <Mail className="w-8 h-8 shrink-0" strokeWidth={1.5} />
    <div>
      <p className="text-xs uppercase tracking-widest opacity-80">Email us</p>
      <p className="font-display text-2xl sm:text-3xl break-all">{COMPANY.email}</p>
    </div>
  </a>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("sent");
      toast.success("Message sent — we'll reply to " + form.email);
    } catch {
      setStatus("failed");
    }
  };

  if (status === "sent")
    return (
      <div className="p-8 rounded-3xl border border-black/5 bg-white/60 text-center" data-testid="contact-success">
        <p className="font-display text-2xl mb-2">Thanks, {form.name.split(" ")[0] || "traveller"}.</p>
        <p className="text-muted-foreground">Your message is on its way. We usually reply within 2 business days.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="p-6 sm:p-8 rounded-3xl border border-black/5 bg-white/60 space-y-4" data-testid="contact-form">
      <h2 className="font-display text-2xl sm:text-3xl mb-2">Send a message</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name" />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={inputCls} data-testid="contact-email" />
      </div>
      <input required value={form.subject} onChange={set("subject")} placeholder="Subject" className={inputCls} data-testid="contact-subject" />
      <textarea required rows={6} value={form.message} onChange={set("message")} placeholder="How can we help?" className={inputCls} data-testid="contact-message" />
      {status === "failed" && (
        <p className="text-sm text-[#C8553D] p-3 rounded-xl bg-[#C8553D]/10" data-testid="contact-error">
          We couldn't send your message right now. Please email us directly at{" "}
          <a href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(form.subject)}`} className="underline underline-offset-4 font-medium" data-testid="contact-error-mailto">
            {COMPANY.email}
          </a>
          .
        </p>
      )}
      <button
        type="submit"
        disabled={status === "sending"}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm disabled:opacity-60 hover:bg-[#15311f] transition-colors"
        data-testid="contact-submit"
      >
        {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
      </button>
    </form>
  );
};

export default function Contact() {
  return (
    <LegalLayout page={CONTACT_PAGE} testId="contact-page" showUpdated={false} before={<EmailCard />}>
      <ContactForm />
    </LegalLayout>
  );
}
